import { Controller } from "@hotwired/stimulus"

// Sonner: doc/sonner/src/index.tsx lines 802-803
const TOAST_WIDTH = 356
const GAP = 14

// Toaster Controller
// Corresponds to Sonner Toaster component (lines 597-900)
// Manages centralized state for all toast positions
export default class extends Controller {
  static targets = ["container", "toast"]  // <ol> and <li> elements
  static values = {
    position: { type: String, default: "bottom-right" },
    theme: { type: String, default: "light" },
    richColors: { type: Boolean, default: false },
    expand: { type: Boolean, default: false },
    duration: { type: Number, default: 4000 },
    visibleToasts: { type: Number, default: 3 },
    closeButton: { type: Boolean, default: false },
  }

  connect() {
    // Sonner lines 627-629: Centralized state at Toaster level
    this.heights = new Map()      // Line 627: const [heights, setHeights] = React.useState([])
    this.expanded = false         // Line 628: const [expanded, setExpanded] = React.useState(false)
    this.interacting = false      // Line 629: const [interacting, setInteracting] = React.useState(false)
    this.toasts = []
  }

  // Sonner lines 828-844: Event handlers attached to each <ol> container
  containerTargetConnected(element) {
    const listeners = {
      // Line 828: onMouseEnter={() => setExpanded(true)}
      mouseenter: () => this.setExpanded(true),

      // Line 829: onMouseMove={() => setExpanded(true)}
      mousemove: () => this.setExpanded(true),

      // Lines 830-835: onMouseLeave with interacting check
      mouseleave: () => {
        // Line 832: Avoid setting expanded to false when interacting with a toast
        if (!this.interacting) {
          this.setExpanded(false)
        }
      },

      // Lines 837-843: onPointerDown to track interaction start
      pointerdown: (event) => {
        const isNotDismissible =
          event.target instanceof HTMLElement && event.target.dataset.dismissible === "false"

        if (isNotDismissible) return
        // Line 842: setInteracting(true)
        this.interacting = true
      },

      // Line 844: onPointerUp to track interaction end
      pointerup: () => {
        this.interacting = false
      },

      // Listen for height updates from toasts
      // Sonner line 160: setHeights((h) => [...h, { toastId, height, position }])
      "toast:height-update": (event) => {
        this.heights.set(event.detail.id, event.detail.height)
        requestAnimationFrame(() => {
          this.updateToastPositions()
        })
      },

      // Listen for toast removal
      // Sonner line 161: return () => setHeights((h) => h.filter(...))
      "toast:removed": (event) => {
        this.heights.delete(event.detail.id)
        this.updateToastPositions()
      },
    }

    Object.entries(listeners).forEach(([event, handler]) => {
      element.addEventListener(event, handler)
    })

    if (!this.containerListeners) this.containerListeners = new WeakMap()
    this.containerListeners.set(element, listeners)
  }

  containerTargetDisconnected(element) {
    const listeners = this.containerListeners?.get(element)
    if (listeners) {
      Object.entries(listeners).forEach(([event, handler]) => {
        element.removeEventListener(event, handler)
      })
      this.containerListeners.delete(element)
    }
  }

  // Sonner lines 154-163: Toast connects and reports height
  // Sonner line 854: defaultRichColors={richColors}
  toastTargetConnected(element) {
    // Sonner line 275: data-rich-colors={toast.richColors ?? defaultRichColors}
    // Inherit rich-colors from toaster if not explicitly set on toast
    if (!element.hasAttribute("data-rich-colors")) {
      element.setAttribute("data-rich-colors", this.richColorsValue.toString())
    }

    // Toast will measure and report its height via custom event
    // Update positions when a new toast is added
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateToastList()
      })
    })
  }

  // Sonner line 161: Cleanup when toast disconnects
  toastTargetDisconnected(element) {
    this.heights.delete(element.id)
    this.updateToastList()
  }

  updateToastList() {
    // Get all toast targets and update their positions
    this.toasts = this.toastTargets || []
    this.updateToastPositions()
  }

  // Sonner lines 128-143, 284-299: Calculate and apply positions to all toasts
  updateToastPositions() {
    // Group toasts by their parent container (each position has its own container)
    const toastsByContainer = new Map()

    this.toasts.forEach((toast) => {
      const container = toast.closest("[data-sonner-toaster]")
      if (container) {
        if (!toastsByContainer.has(container)) {
          toastsByContainer.set(container, [])
        }
        toastsByContainer.get(container).push(toast)
      }
    })

    // Update positions for each container separately
    toastsByContainer.forEach((containerToasts, container) => {
      containerToasts.forEach((toast, index) => {
        // Lines 128-137: Calculate toastsHeightBefore (sum of heights in THIS container only)
        let toastsHeightBefore = 0
        for (let i = 0; i < index; i++) {
          const prevToastId = containerToasts[i].id
          const prevHeight = this.heights.get(prevToastId)
          if (prevHeight) {
            toastsHeightBefore += prevHeight
          }
        }

        // Line 143: offset.current = heightIndex * gap + toastsHeightBefore
        const offset = index * GAP + toastsHeightBefore

        // Lines 284-292: Update data attributes
        const isFront = index === 0
        const isVisible = index + 1 <= this.visibleToastsValue

        toast.setAttribute("data-index", index)                              // Line 284
        toast.setAttribute("data-front", isFront.toString())                 // Line 285
        toast.setAttribute("data-visible", isVisible.toString())             // Line 281
        toast.setAttribute("data-expanded", this.expanded.toString())        // Line 292

        // Lines 296-299: Update CSS variables
        toast.style.setProperty("--index", index)                            // Line 296
        toast.style.setProperty("--toasts-before", index)                    // Line 297
        toast.style.setProperty("--z-index", containerToasts.length - index) // Line 298
        toast.style.setProperty("--offset", `${offset}px`)                   // Line 299
      })

      // Line 801: '--front-toast-height': Update for THIS container
      if (containerToasts.length > 0) {
        const frontToastId = containerToasts[0].id
        const frontHeight = this.heights.get(frontToastId)
        container.style.setProperty("--front-toast-height", `${frontHeight || 0}px`)
        container.style.setProperty("--width", `${TOAST_WIDTH}px`)
      }
    })
  }

  // Sonner lines 732-736: Expand state management
  setExpanded(expanded) {
    // Line 734: if (toasts.length <= 1) setExpanded(false)
    if (this.toasts.length <= 1) {
      this.expanded = false
      return
    }

    this.expanded = expanded
    // Update all toasts with new expanded state
    this.updateToastPositions()
  }
}
